import { Crown, Diamond, Sparkles, Check, MessageCircle } from "lucide-react";
import { MEMBERSHIPS } from "@/data/salonData";
import { openWhatsApp, bookServiceMessage } from "@/lib/whatsapp";
import { Reveal } from "@/lib/useReveal";

const TIER_META = {
  Gold: { Icon: Crown, color: "#d4af37" },
  Diamond: { Icon: Diamond, color: "#b56477" },
  Platinum: { Icon: Sparkles, color: "#2a1b18" },
};

export default function Membership() {
  return (
    <section
      id="membership"
      data-testid="membership-section"
      className="relative py-20 md:py-28 overflow-hidden"
    >
      {/* deco bg */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#fff5f8] via-white to-white">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-radial from-[#f9d0da]/40 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="text-center max-w-3xl mx-auto">
          <span className="heading-flourish">Premium Membership</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2a1b18] font-medium mt-4 leading-tight">
            Become A <span className="text-gradient-gold italic">Member,</span>
            <br />
            Be Forever <span className="text-gradient-rose italic">Glowing</span>
          </h2>
          <p className="mt-5 text-[#5c4642] text-base md:text-lg">
            Curated plans for women who refuse to skip self-care.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-7">
          {MEMBERSHIPS.map((m, i) => {
            const { Icon, color } = TIER_META[m.tier];
            const featured = m.mostPopular;
            return (
              <Reveal key={m.tier} delay={i * 120}>
                <div
                  data-testid={`membership-card-${m.tier.toLowerCase()}`}
                  className={`relative rounded-3xl p-8 card-lift h-full flex flex-col ${
                    featured
                      ? "bg-gradient-to-b from-[#2a1b18] to-[#1a0f0d] text-white glow-gold scale-100 md:scale-[1.04]"
                      : "bg-white border border-[#d98a99]/15"
                  }`}
                >
                  {featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold bg-gradient-to-r from-[#d4af37] to-[#e7c875] text-[#2a1b18] shadow-lg">
                      Most Popular
                    </div>
                  )}

                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${
                      featured ? "bg-white/10" : "bg-gradient-to-br from-[#fff0f5] to-[#f9d0da]"
                    }`}
                  >
                    <Icon
                      size={26}
                      style={{ color: featured ? "#d4af37" : color }}
                    />
                  </div>

                  <div
                    className={`text-[11px] uppercase tracking-[0.25em] font-semibold ${
                      featured ? "text-[#d4af37]" : "text-[#b56477]"
                    }`}
                  >
                    {m.duration}
                  </div>
                  <div className="font-display text-3xl md:text-4xl font-medium mt-1">
                    {m.tier}
                  </div>

                  <div className="mt-5 flex items-baseline gap-1">
                    <span
                      className={`font-display text-5xl font-medium ${
                        featured ? "text-white" : "text-[#2a1b18]"
                      }`}
                    >
                      ₹{m.price.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div
                    className={`text-xs mt-1 ${
                      featured ? "text-white/60" : "text-[#5c4642]"
                    }`}
                  >
                    Billed {m.duration.toLowerCase()}
                  </div>

                  <ul className="mt-6 space-y-3 flex-1">
                    {m.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2.5 text-sm">
                        <Check
                          size={16}
                          className={`mt-0.5 flex-shrink-0 ${
                            featured ? "text-[#d4af37]" : "text-[#b56477]"
                          }`}
                        />
                        <span
                          className={featured ? "text-white/85" : "text-[#5c4642]"}
                        >
                          {perk}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() =>
                      openWhatsApp(
                        bookServiceMessage(`${m.tier} Membership (${m.duration})`, m.price)
                      )
                    }
                    data-testid={`subscribe-${m.tier.toLowerCase()}`}
                    className={`mt-7 inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-full text-sm font-medium transition-all ${
                      featured
                        ? "btn-gold"
                        : "btn-outline-rose"
                    }`}
                  >
                    <MessageCircle size={14} />
                    Subscribe Now
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
